import { produce } from 'immer';
import { Layout, Layouts } from 'react-grid-layout';
import { GridHeightUnits, WidgetSizes, gridColumns, widgetSizes } from './constants';
import { DashboardBreakpoints } from './types';

export type WidgetPriority = 'critical' | 'urgent' | 'high' | 'medium' | 'low' | 'minimal' | 'hidden';

export type LayoutPattern = 'default' | 'mixed' | 'cascade' | 'balanced';

/**
 * Clone a grid.
 * @param {number[][]} grid - The grid to clone.
 * @returns {number[][]} A deep copy of the grid.
 */
const cloneGrid = (grid: number[][]): number[][] => grid.map((row) => row.slice(0));

/**
 * Place an element in the grid.
 * @param {number[][]} gridMatrix - The grid to place the element in.
 * @param {number} startX - The x-coordinate to start placing the element.
 * @param {number} startY - The y-coordinate to start placing the element.
 * @param {number} elementHeight - The height of the element.
 * @param {number} elementWidth - The width of the element.
 * @param {number} totalColumns - The number of columns in the grid.
 * @returns {number[][] | false} The updated grid if the element is placed successfully, otherwise false.
 */
const putElement = (
  gridMatrix: number[][],
  startX: number,
  startY: number,
  elementHeight: number,
  elementWidth: number,
  totalColumns: number
): number[][] | false => {
  if (startX + elementWidth > totalColumns) return false;
  const newGridMatrix = cloneGrid(gridMatrix);
  for (let rowIndex = startY; rowIndex < startY + elementHeight; rowIndex++) {
    if (newGridMatrix[rowIndex] === undefined) newGridMatrix[rowIndex] = Array(totalColumns).fill(0);
    for (let columnIndex = startX; columnIndex < startX + elementWidth; columnIndex++) {
      if (newGridMatrix[rowIndex][columnIndex] === 1) return false;
      newGridMatrix[rowIndex][columnIndex] = 1;
    }
  }
  return newGridMatrix;
};

/**
 * Add filler to the grid.
 * @param {number[][]} gridMatrix - The grid to add filler to.
 * @param {number} totalColumns - The number of columns in the grid.
 */
const addFiller = (gridMatrix: number[][], totalColumns: number): void => {
  const reversedGridClone = cloneGrid(gridMatrix).reverse();
  for (let rowIndex = 0; rowIndex < reversedGridClone.length; rowIndex++) {
    for (let columnIndex = 0; columnIndex < totalColumns; columnIndex++) {
      if (reversedGridClone[rowIndex][columnIndex] === 0) {
        //@ts-ignore
        if (rowIndex === 0 || reversedGridClone[rowIndex - 1][columnIndex] === false) {
          //@ts-ignore
          reversedGridClone[rowIndex][columnIndex] = false;
        }
      }
    }
  }
};

interface WidgetElement {
  h: number;
  w: number;
  y?: number;
  x?: number;
}

/**
 * TODO: It might need a bit more improvement.
 * This function implements a bin packing algorithm that acts similarly to a css grid-auto-flow: dense;
 * @param {WidgetElement[]} widgetElementsList - The list of elements to package.
 * @param {number} totalColumns - The number of columns in the grid.
 * @returns {WidgetElement[]} The list of elements with their positions in the grid.
 * @throws An error if an element does not fit in the grid.
 */
export const packageElements = (widgetElementsList: WidgetElement[], totalColumns: number): WidgetElement[] => {
  let gridMatrix = [Array(totalColumns).fill(0)];
  const widgetElements = widgetElementsList.slice(0);
  widgetElements.forEach((widgetItem) => {
    let isPlaced = false;
    for (let rowIndex = 0; rowIndex < gridMatrix.length; rowIndex++) {
      for (let columnIndex = 0; columnIndex < totalColumns; columnIndex++) {
        if (gridMatrix[rowIndex][columnIndex] === 0) {
          const placementResult = putElement(
            gridMatrix,
            columnIndex,
            rowIndex,
            widgetItem.h,
            widgetItem.w,
            totalColumns
          );
          if (placementResult !== false) {
            widgetItem.y = rowIndex;
            widgetItem.x = columnIndex;
            gridMatrix = placementResult;
            isPlaced = true;
            break;
          }
        }
      }
      if (isPlaced) break;
    }
    if (!isPlaced) {
      const currentGridLength = gridMatrix.length;
      const placementResult = putElement(gridMatrix, 0, gridMatrix.length, widgetItem.h, widgetItem.w, totalColumns);
      if (placementResult === false) throw new Error('The element does not fit!');
      widgetItem.y = currentGridLength;
      widgetItem.x = 0;
      gridMatrix = placementResult;
    }
  });
  addFiller(gridMatrix, totalColumns);
  return widgetElements;
};

export type WidgetConfig = {
  id: string;
  enabled: boolean;
  size: Record<DashboardBreakpoints, WidgetSizes> | WidgetSizes;
  positions?: Record<DashboardBreakpoints, Layout>;
  priority?: WidgetPriority;
};

function findAvailablePosition(
  currentLayout: Layout[],
  requiredWidth: number,
  requiredHeight: number,
  totalColumns: number
): { x: number; y: number } {
  const occupiedCells = new Set<string>();

  for (const widget of currentLayout) {
    for (let rowIndex = widget.y; rowIndex < widget.y + widget.h; rowIndex++) {
      for (let columnIndex = widget.x; columnIndex < widget.x + widget.w; columnIndex++) {
        occupiedCells.add(`${columnIndex},${rowIndex}`);
      }
    }
  }

  for (let rowIndex = 0; rowIndex < Number.MAX_SAFE_INTEGER; rowIndex++) {
    for (let columnIndex = 0; columnIndex <= totalColumns - requiredWidth; columnIndex++) {
      if (isPositionAvailable(occupiedCells, columnIndex, rowIndex, requiredWidth, requiredHeight)) {
        return { x: columnIndex, y: rowIndex };
      }
    }
  }
  return { x: 0, y: 0 }; // Default fallback
}

function isPositionAvailable(
  occupiedCells: Set<string>,
  startX: number,
  startY: number,
  requiredWidth: number,
  requiredHeight: number
): boolean {
  for (let heightOffset = 0; heightOffset < requiredHeight; heightOffset++) {
    for (let widthOffset = 0; widthOffset < requiredWidth; widthOffset++) {
      if (occupiedCells.has(`${startX + widthOffset},${startY + heightOffset}`)) {
        return false;
      }
    }
  }
  return true;
}

export function tidyDashboardLayout(
  widgetConfigs: WidgetConfig[],
  breakpoints: DashboardBreakpoints[] = ['large', 'medium', 'small', 'extraSmall'],
  layoutPattern: LayoutPattern = 'mixed'
): WidgetConfig[] {
  return produce(widgetConfigs, (draftWidgetConfigs) => {
    const enabledWidgets = draftWidgetConfigs.filter((widget) => widget.enabled);

    breakpoints.forEach((currentBreakpoint) => {
      const widgetsForPacking = enabledWidgets.map((widget) => ({
        id: widget.id,
        size: (typeof widget.size === 'string' ? widget.size : widget.size[currentBreakpoint]) as WidgetSizes,
        priority: widget.priority || 'medium',
      }));

      const packedLayouts = optimizedWidgetPacking(widgetsForPacking, gridColumns[currentBreakpoint], layoutPattern);

      packedLayouts.forEach((layoutItem) => {
        const targetWidget = draftWidgetConfigs.find((w) => w.id === layoutItem.i);
        if (targetWidget) {
          if (!targetWidget.positions) targetWidget.positions = {} as Record<DashboardBreakpoints, Layout>;
          targetWidget.positions[currentBreakpoint] = layoutItem;
        }
      });
    });
  });
}

export function optimizedWidgetPacking(
  widgetsToPlace: Array<{ id: string; size: WidgetSizes; priority?: WidgetPriority }>,
  totalColumns: number,
  layoutPattern: LayoutPattern = 'mixed'
): Layout[] {
  const prioritySortedWidgets = sortWidgetsByPriorityAndPattern(widgetsToPlace, layoutPattern);

  const gridMatrix: number[][] = [];
  const finalLayouts: Layout[] = [];

  function canPlaceWidget(startX: number, startY: number, requiredWidth: number, requiredHeight: number): boolean {
    if (startX + requiredWidth > totalColumns) return false;
    for (let rowIndex = startY; rowIndex < startY + requiredHeight; rowIndex++) {
      if (!gridMatrix[rowIndex]) gridMatrix[rowIndex] = new Array(totalColumns).fill(0);
      for (let columnIndex = startX; columnIndex < startX + requiredWidth; columnIndex++) {
        if (gridMatrix[rowIndex][columnIndex] === 1) return false;
      }
    }
    return true;
  }

  function placeWidgetInGrid(
    widget: { id: string; size: WidgetSizes; priority?: WidgetPriority },
    xPosition: number,
    yPosition: number
  ) {
    const { w: widgetWidth, h: widgetHeight } = widgetSizes[widget.size];
    for (let rowIndex = yPosition; rowIndex < yPosition + widgetHeight; rowIndex++) {
      for (let columnIndex = xPosition; columnIndex < xPosition + widgetWidth; columnIndex++) {
        gridMatrix[rowIndex][columnIndex] = 1;
      }
    }
    finalLayouts.push({
      i: widget.id,
      x: xPosition,
      y: yPosition,
      w: widgetWidth,
      h: widgetHeight,
    });
  }

  // Apply pattern-specific placement logic
  if (layoutPattern === 'cascade') {
    placeCascadePattern(prioritySortedWidgets, totalColumns, canPlaceWidget, placeWidgetInGrid);
  } else if (layoutPattern === 'balanced') {
    placeBalancedPattern(prioritySortedWidgets, totalColumns, canPlaceWidget, placeWidgetInGrid);
  } else {
    // Default and mixed patterns use the original logic with priority consideration
    prioritySortedWidgets.forEach((widget) => {
      const { w: widgetWidth, h: widgetHeight } = widgetSizes[widget.size];
      let isWidgetPlaced = false;

      for (let rowIndex = 0; !isWidgetPlaced; rowIndex++) {
        for (let columnIndex = 0; columnIndex < totalColumns; columnIndex++) {
          if (canPlaceWidget(columnIndex, rowIndex, widgetWidth, widgetHeight)) {
            placeWidgetInGrid(widget, columnIndex, rowIndex);
            isWidgetPlaced = true;
            break;
          }
        }
      }
    });
  }

  return finalLayouts;
}

export function addWidgetToLayout(
  existingWidgetConfig: WidgetConfig[],
  newWidget: {
    id: string;
    size: Record<DashboardBreakpoints, WidgetSizes> | WidgetSizes;
    priority?: WidgetPriority;
  }
): WidgetConfig[] {
  const updatedWidgetConfig = [...existingWidgetConfig];

  for (const breakpointName in gridColumns) {
    const widgetSize =
      typeof newWidget.size === 'string'
        ? widgetSizes[newWidget.size]
        : widgetSizes[newWidget.size[breakpointName as DashboardBreakpoints]];
    const currentLayout = updatedWidgetConfig
      .map((widget) => widget.positions?.[breakpointName as DashboardBreakpoints])
      .filter(Boolean) as Layout[];
    const availablePosition = findAvailablePosition(
      currentLayout,
      widgetSize.w,
      widgetSize.h,
      gridColumns[breakpointName as DashboardBreakpoints]
    );

    const newWidgetLayoutPosition: Layout = {
      i: newWidget.id,
      x: availablePosition.x,
      y: availablePosition.y,
      w: widgetSize.w,
      h: widgetSize.h,
    };

    const existingWidget = updatedWidgetConfig.find((widget) => widget.id === newWidget.id) as WidgetConfig;

    if (existingWidget) {
      if (!existingWidget.positions) {
        existingWidget.positions = {} as Record<DashboardBreakpoints, Layout>;
      }

      existingWidget.positions = {
        ...existingWidget.positions,
        [breakpointName as DashboardBreakpoints]: newWidgetLayoutPosition,
      };

      if (newWidget.priority) {
        existingWidget.priority = newWidget.priority;
      }
    } else {
      updatedWidgetConfig.push({
        id: newWidget.id,
        enabled: true,
        size: newWidget.size,
        priority: newWidget.priority || 'medium',
        positions: {
          [breakpointName as DashboardBreakpoints]: newWidgetLayoutPosition,
        } as Record<DashboardBreakpoints, Layout>,
      });
    }
  }

  return updatedWidgetConfig;
}

/**
 * Tries to place an element in the grid.
 * @param gridMatrix - The grid to place the element in.
 * @param startX - The starting x-coordinate.
 * @param startY - The starting y-coordinate.
 * @param elementWidth - The width of the element.
 * @param elementHeight - The height of the element.
 * @param totalColumns - The total number of columns in the grid.
 * @returns A new grid with the element placed, or false if the placement is not possible.
 */
function placeElement(
  gridMatrix: number[][],
  startX: number,
  startY: number,
  elementWidth: number,
  elementHeight: number,
  totalColumns: number
): number[][] | false {
  if (startX + elementWidth > totalColumns) {
    return false;
  }
  gridMatrix = cloneGrid(gridMatrix);

  for (let rowIndex = startY; rowIndex < startY + elementHeight; rowIndex++) {
    if (gridMatrix[rowIndex] === undefined) {
      gridMatrix[rowIndex] = Array(totalColumns).fill(0);
    }
    for (let columnIndex = startX; columnIndex < startX + elementWidth; columnIndex++) {
      if (gridMatrix[rowIndex][columnIndex] === 1) {
        return false;
      }
      gridMatrix[rowIndex][columnIndex] = 1;
    }
  }
  return gridMatrix;
}

const maxColumnsPossible = 4;

/**
 * Finds the optimal position for a new element in the grid.
 * @param currentLayout - The current layout of the dashboard.
 * @param totalColumns - The total number of columns in the grid.
 * @returns The optimal position and size for the new element.
 */
export function findOptimalPosition(
  currentLayout: Layout[],
  totalColumns: number
): { x: number; y: number; width: number; height: number } {
  if (currentLayout.length === 0) {
    return { x: 0, y: 0, width: 2, height: GridHeightUnits.medium };
  }

  const gridMatrix: number[][] = [];
  let maxYPosition = 0;

  currentLayout.forEach((widget) => {
    for (let rowIndex = widget.y; rowIndex < widget.y + widget.h; rowIndex++) {
      if (!gridMatrix[rowIndex]) {
        gridMatrix[rowIndex] = Array(totalColumns).fill(0);
      }
      for (let columnIndex = widget.x; columnIndex < widget.x + widget.w; columnIndex++) {
        gridMatrix[rowIndex][columnIndex] = 1;
      }
    }

    maxYPosition = Math.max(maxYPosition, widget.y + widget.h);
  });

  for (let rowIndex = 0; rowIndex <= maxYPosition; rowIndex++) {
    for (let columnIndex = 0; columnIndex < totalColumns; columnIndex++) {
      if (!gridMatrix[rowIndex] || gridMatrix[rowIndex][columnIndex] === 0) {
        let maxAvailableWidth = 0;
        for (
          let widthCheck = columnIndex;
          widthCheck < totalColumns && (!gridMatrix[rowIndex] || gridMatrix[rowIndex][widthCheck] === 0);
          widthCheck++
        ) {
          maxAvailableWidth++;
        }

        for (const possibleHeight of [GridHeightUnits.tall, GridHeightUnits.medium, GridHeightUnits.small]) {
          let canFitHeight = true;
          for (
            let heightCheck = rowIndex;
            heightCheck < rowIndex + possibleHeight && heightCheck <= maxYPosition;
            heightCheck++
          ) {
            if (!gridMatrix[heightCheck]) {
              gridMatrix[heightCheck] = Array(totalColumns).fill(0);
            }
            if (gridMatrix[heightCheck][columnIndex] === 1) {
              canFitHeight = false;
              break;
            }
          }

          if (canFitHeight) {
            const placementResult = placeElement(
              gridMatrix,
              columnIndex,
              rowIndex,
              Math.min(maxAvailableWidth, maxColumnsPossible),
              possibleHeight,
              totalColumns
            );
            if (placementResult !== false) {
              return {
                x: columnIndex,
                y: rowIndex,
                width: Math.min(maxAvailableWidth, maxColumnsPossible),
                height: possibleHeight,
              };
            }
          }
        }
      }
    }
  }

  return { x: 0, y: maxYPosition + 1, width: 2, height: GridHeightUnits.medium };
}

/***
 * Widgets or PLG Widgets should be enabled if `hasAccess` or `hasFeature` is undefined.
 * This is just a small helper to abstract that logic.
 */
export const isEnabled = (feature: boolean | undefined) => feature !== false;

export const transformToLayouts = (widgets: WidgetConfig[]): Layouts => {
  const layouts: Layouts = { large: [], medium: [], small: [], extraSmall: [] };

  for (const widget of widgets) {
    if (!widget.enabled) continue;

    const size =
      typeof widget.size === 'string'
        ? { large: widget.size, medium: widget.size, small: widget.size, extraSmall: widget.size }
        : widget.size;

    for (const breakpoint of Object.keys(size) as DashboardBreakpoints[]) {
      const widgetSize = widgetSizes[size[breakpoint]];
      const position = widget.positions?.[breakpoint] || { x: 0, y: 0 };

      layouts[breakpoint].push({
        i: widget.id,
        w: widgetSize.w,
        h: widgetSize.h,
        x: position.x,
        y: position.y,
      });
    }
  }

  return layouts;
};

function sortWidgetsByPriorityAndPattern(
  widgetsToSort: Array<{ id: string; size: WidgetSizes; priority?: WidgetPriority }>,
  layoutPattern: LayoutPattern
): Array<{ id: string; size: WidgetSizes; priority?: WidgetPriority }> {
  const priorityOrderMapping = { critical: 7, urgent: 6, high: 5, medium: 4, low: 3, minimal: 2, hidden: 1 };

  return widgetsToSort.sort((widgetA, widgetB) => {
    const priorityValueA = priorityOrderMapping[widgetA.priority || 'medium'];
    const priorityValueB = priorityOrderMapping[widgetB.priority || 'medium'];

    if (priorityValueA !== priorityValueB) {
      return priorityValueB - priorityValueA;
    }

    const widgetASizeInfo = widgetSizes[widgetA.size];
    const widgetBSizeInfo = widgetSizes[widgetB.size];

    switch (layoutPattern) {
      case 'mixed': {
        const widgetAArea = widgetASizeInfo.w * widgetASizeInfo.h;
        const widgetBArea = widgetBSizeInfo.w * widgetBSizeInfo.h;
        const isWidgetALarge = widgetAArea > 4;
        const isWidgetBLarge = widgetBArea > 4;

        if (isWidgetALarge !== isWidgetBLarge) {
          return Math.random() - 0.5;
        }
        return widgetBArea - widgetAArea;
      }

      case 'cascade': {
        return widgetBSizeInfo.w - widgetASizeInfo.w || widgetBSizeInfo.h - widgetASizeInfo.h;
      }
      case 'balanced': {
        const areaComparison = widgetBSizeInfo.w * widgetBSizeInfo.h - widgetASizeInfo.w * widgetASizeInfo.h;
        if (areaComparison !== 0) return areaComparison;

        const widgetAAspectRatio = Math.abs(widgetASizeInfo.w / widgetASizeInfo.h - 1);
        const widgetBAspectRatio = Math.abs(widgetBSizeInfo.w / widgetBSizeInfo.h - 1);
        return widgetAAspectRatio - widgetBAspectRatio;
      }
      default: {
        return widgetBSizeInfo.h - widgetASizeInfo.h || widgetBSizeInfo.w - widgetASizeInfo.w;
      }
    }
  });
}

function placeCascadePattern(
  widgetsToPlace: Array<{ id: string; size: WidgetSizes; priority?: WidgetPriority }>,
  totalColumns: number,
  canPlaceWidget: (x: number, y: number, w: number, h: number) => boolean,
  placeWidgetInGrid: (
    widget: { id: string; size: WidgetSizes; priority?: WidgetPriority },
    x: number,
    y: number
  ) => void
): void {
  let currentXPosition = 0;
  let currentYPosition = 0;
  let currentRowMaxHeight = 0;

  widgetsToPlace.forEach((widget) => {
    const { w: widgetWidth, h: widgetHeight } = widgetSizes[widget.size];
    let isWidgetPlaced = false;

    if (
      currentXPosition + widgetWidth <= totalColumns &&
      canPlaceWidget(currentXPosition, currentYPosition, widgetWidth, widgetHeight)
    ) {
      placeWidgetInGrid(widget, currentXPosition, currentYPosition);
      currentXPosition += widgetWidth;
      currentRowMaxHeight = Math.max(currentRowMaxHeight, widgetHeight);
      isWidgetPlaced = true;
    } else {
      currentYPosition += currentRowMaxHeight || 1;
      currentXPosition = 0;
      currentRowMaxHeight = 0;

      if (canPlaceWidget(currentXPosition, currentYPosition, widgetWidth, widgetHeight)) {
        placeWidgetInGrid(widget, currentXPosition, currentYPosition);
        currentXPosition += widgetWidth;
        currentRowMaxHeight = widgetHeight;
        isWidgetPlaced = true;
      }
    }

    if (!isWidgetPlaced) {
      for (let rowIndex = 0; !isWidgetPlaced; rowIndex++) {
        for (let columnIndex = 0; columnIndex < totalColumns; columnIndex++) {
          if (canPlaceWidget(columnIndex, rowIndex, widgetWidth, widgetHeight)) {
            placeWidgetInGrid(widget, columnIndex, rowIndex);
            isWidgetPlaced = true;
            break;
          }
        }
      }
    }
  });
}

function placeBalancedPattern(
  widgetsToPlace: Array<{ id: string; size: WidgetSizes; priority?: WidgetPriority }>,
  totalColumns: number,
  canPlaceWidget: (x: number, y: number, w: number, h: number) => boolean,
  placeWidgetInGrid: (
    widget: { id: string; size: WidgetSizes; priority?: WidgetPriority },
    x: number,
    y: number
  ) => void
): void {
  const largeWidgets = widgetsToPlace.filter((widget) => {
    const widgetSizeInfo = widgetSizes[widget.size];
    return widgetSizeInfo.w * widgetSizeInfo.h > 4;
  });
  const mediumWidgets = widgetsToPlace.filter((widget) => {
    const widgetSizeInfo = widgetSizes[widget.size];
    const widgetArea = widgetSizeInfo.w * widgetSizeInfo.h;
    return widgetArea > 2 && widgetArea <= 4;
  });
  const smallWidgets = widgetsToPlace.filter((widget) => {
    const widgetSizeInfo = widgetSizes[widget.size];
    return widgetSizeInfo.w * widgetSizeInfo.h <= 2;
  });

  const balancedWidgetOrder = [
    ...largeWidgets.slice(0, Math.ceil(largeWidgets.length / 2)),
    ...mediumWidgets.slice(0, Math.ceil(mediumWidgets.length / 2)),
    ...smallWidgets.slice(0, Math.ceil(smallWidgets.length / 2)),
    ...largeWidgets.slice(Math.ceil(largeWidgets.length / 2)),
    ...mediumWidgets.slice(Math.ceil(mediumWidgets.length / 2)),
    ...smallWidgets.slice(Math.ceil(smallWidgets.length / 2)),
  ];

  balancedWidgetOrder.forEach((widget) => {
    const { w: widgetWidth, h: widgetHeight } = widgetSizes[widget.size];
    let isWidgetPlaced = false;

    for (let rowIndex = 0; !isWidgetPlaced; rowIndex++) {
      for (let columnIndex = 0; columnIndex < totalColumns; columnIndex++) {
        if (canPlaceWidget(columnIndex, rowIndex, widgetWidth, widgetHeight)) {
          placeWidgetInGrid(widget, columnIndex, rowIndex);
          isWidgetPlaced = true;
          break;
        }
      }
    }
  });
}

export function updateWidgetPriority(
  currentWidgetConfigs: WidgetConfig[],
  targetWidgetId: string,
  newPriority: WidgetPriority
): WidgetConfig[] {
  return produce(currentWidgetConfigs, (draftWidgetConfigs) => {
    const targetWidget = draftWidgetConfigs.find((widget) => widget.id === targetWidgetId);
    if (targetWidget) {
      targetWidget.priority = newPriority;
    }
  });
}

export function updateMultipleWidgetPriorities(
  currentWidgetConfigs: WidgetConfig[],
  priorityUpdates: Record<string, WidgetPriority>
): WidgetConfig[] {
  return produce(currentWidgetConfigs, (draftWidgetConfigs) => {
    Object.entries(priorityUpdates).forEach(([widgetId, newPriority]) => {
      const targetWidget = draftWidgetConfigs.find((widget) => widget.id === widgetId);
      if (targetWidget) {
        targetWidget.priority = newPriority;
      }
    });
  });
}

export function getWidgetsByPriority(widgetConfigs: WidgetConfig[]): Record<WidgetPriority, WidgetConfig[]> {
  const priorityGroupedWidgets: Record<WidgetPriority, WidgetConfig[]> = {
    critical: [],
    urgent: [],
    high: [],
    medium: [],
    low: [],
    minimal: [],
    hidden: [],
  };

  widgetConfigs.forEach((widget) => {
    const widgetPriority = widget.priority || 'medium';
    priorityGroupedWidgets[widgetPriority].push(widget);
  });

  return priorityGroupedWidgets;
}

export type PredefinedPriorityConfig = Record<string, WidgetPriority>;

export interface PriorityPreset {
  id: string;
  name: string;
  description: string;
  config: PredefinedPriorityConfig;
}

export function applyPredefinedPriorities(
  currentWidgetConfigs: WidgetConfig[],
  predefinedPriorityConfig: PredefinedPriorityConfig
): WidgetConfig[] {
  return produce(currentWidgetConfigs, (draftWidgetConfigs) => {
    draftWidgetConfigs.forEach((widget) => {
      if (predefinedPriorityConfig[widget.id]) {
        widget.priority = predefinedPriorityConfig[widget.id];
      }
    });
  });
}

export function initializeWithPredefinedPriorities(
  currentWidgetConfigs: WidgetConfig[],
  predefinedPriorityConfig?: PredefinedPriorityConfig
): WidgetConfig[] {
  if (!predefinedPriorityConfig) {
    return currentWidgetConfigs;
  }

  return applyPredefinedPriorities(currentWidgetConfigs, predefinedPriorityConfig);
}

export function extractPriorityConfig(currentWidgetConfigs: WidgetConfig[]): PredefinedPriorityConfig {
  const extractedPriorityConfig: PredefinedPriorityConfig = {};

  currentWidgetConfigs.forEach((widget) => {
    if (widget.priority && widget.priority !== 'medium') {
      extractedPriorityConfig[widget.id] = widget.priority;
    }
  });

  return extractedPriorityConfig;
}
